import { useMemo, useState } from "react";
import { Network, Pencil, Plus, Search, Trash2 } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import type { RedSummary } from "./schemas";
import { useDeleteRed, useListRedes, useRed } from "./api";
import { RedFormDialog } from "./RedFormDialog";
// rc140 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

export function RedesListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: editing } = useRed(editingId);

  const filters = useMemo(() => ({ page, size: 20, search }), [page, search]);
  const { data, isLoading, isFetching } = useListRedes(filters);
  const deleteMut = useDeleteRed();

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleDelete(r: RedSummary) {
    if (!confirm(`¿Eliminar la red "${r.nombre}" (${r.cidr})?\nDebe no tener IPs asignadas a contratos activos.`)) return;
    try { await deleteMut.mutateAsync(r.id); }
    catch (e) { alert(`Error: ${(e as { message?: string })?.message}`); }
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Redes</h2>
          <p className="text-xs text-muted-foreground">{data ? `${data.total} ${data.total === 1 ? "red" : "redes"}` : "Cargando…"}</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> <span className="max-md:hidden">Nueva red</span><span className="md:hidden">Nueva</span></Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} placeholder="Buscar por nombre o CIDR…" className="pl-8" />
        </div>
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>{search ? "Sin coincidencias." : "Crea la primera subred para asignar IPs."}</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((r) => (
              <MobileCard
                key={r.id}
                onClick={() => setEditingId(r.id)}
                title={r.nombre}
                subtitle={<span className="font-mono">{r.cidr}</span>}
                right={<Badge variant={r.activo ? "success" : "secondary"}>{r.activo ? "activa" : "inactiva"}</Badge>}
                actions={
                  <>
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(r.id)}><Pencil size={13} /> Editar</Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(r)}><Trash2 size={13} /> Eliminar</Button>
                  </>
                }
              >
                <MobileMeta label="Gateway" mono>{r.gateway ?? "—"}</MobileMeta>
                <MobileMeta label="Router">{r.router ? r.router.nombre : <span className="italic text-muted-foreground">sin asignar</span>}</MobileMeta>
                <MobileMeta label="Uso">{r.uso_interno ? <Badge variant="warning">interno</Badge> : <Badge variant="default">clientes</Badge>}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>CIDR</TableHead>
              <TableHead>Gateway</TableHead>
              <TableHead>Router</TableHead>
              <TableHead>Uso</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState icon={Network} title="Sin redes" description={search ? "Sin coincidencias." : "Crea la primera subred para asignar IPs."}
                  action={!search ? <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> Nueva red</Button> : undefined} />
              </TableEmpty>
            ) : (
              items.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setEditingId(r.id)}>
                  <TableCell className="font-medium">{r.nombre}</TableCell>
                  <TableCell className="font-mono">{r.cidr}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">{r.gateway ?? "—"}</TableCell>
                  <TableCell>
                    {r.router ? (
                      <span className="text-xs">{r.router.nombre}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">sin asignar</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.uso_interno ? <Badge variant="warning">interno</Badge> : <Badge variant="default">clientes</Badge>}
                  </TableCell>
                  <TableCell><Badge variant={r.activo ? "success" : "secondary"}>{r.activo ? "activa" : "inactiva"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => setEditingId(r.id)} title="Editar"><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(r)} title="Eliminar"><Trash2 size={14} className="text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">Página {data?.page ?? 1} de {totalPages}{isFetching && " · actualizando…"}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        )}
      </div>
      )}

      <RedFormDialog open={createOpen} onOpenChange={setCreateOpen} red={null} />
      <RedFormDialog open={!!editingId && !!editing} onOpenChange={(v) => { if (!v) setEditingId(null); }} red={editing ?? null} />
    </div>
  );
}
